import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type z } from 'zod'

import { syncDraftToJsonBin } from '#app/domains/mocra/sync/jsonbin.client.ts'

import { type SectionKey } from '#app/routes/mocra+/_form+/intake/intake-draft.ts'
import {
	useIntakeDraftState,
	useIntakeDraftStore,
	type SetSectionStateAction,
} from '#app/routes/mocra+/_form+/intake-context.tsx'
import {
	getValueAtPath,
	setValueAtPath,
	updateValueAtPath,
} from '#app/routes/mocra+/_form+/utils/object-path.ts'

type SectionFormStatus = 'idle' | 'valid' | 'invalid'

export type SectionFormSubmission<Values> =
	| {
			readonly valid: true
			readonly data: Values
	  }
	| {
			readonly valid: false
			readonly issues: readonly z.ZodIssue[]
	  }

export type UseSectionFormResult<Values> = {
	readonly values: Values
	readonly lastValidValues: Values
	readonly setValues: (action: SetSectionStateAction<Values>) => void
	readonly submit: () => SectionFormSubmission<Values>
	readonly reset: () => void
	readonly clear: () => void
	readonly status: SectionFormStatus
	readonly errors: readonly z.ZodIssue[]
	readonly getFieldErrors: (path: string) => string[]
	readonly hasStoredValue: boolean
	readonly setFieldValue: (path: string, value: unknown) => void
	readonly updateFieldValue: (
		path: string,
		updater: (current: unknown) => unknown,
	) => void
	readonly getFieldValue: <T = unknown>(path: string) => T | undefined
}

type UseSectionFormOptions<Schema extends z.ZodTypeAny> = {
	readonly section: SectionKey
	readonly schema: Schema
	readonly defaultValue: z.infer<Schema>
	readonly onValidSubmit?: (value: z.infer<Schema>) => void
}

export function useSectionForm<Schema extends z.ZodTypeAny>(
	options: UseSectionFormOptions<Schema>,
): UseSectionFormResult<z.infer<Schema>> {
	const { section, schema, defaultValue, onValidSubmit } = options
	const store = useIntakeDraftStore()
	const draftState = useIntakeDraftState()

	type Values = z.infer<Schema>

	const sanitizedDefault = useMemo(() => {
		const result = schema.safeParse(defaultValue)
		if (result.success) return result.data
		return defaultValue
	}, [defaultValue, schema])

	const storedValue = draftState.sections[section] as Values | undefined
	const hasStoredSection = Object.prototype.hasOwnProperty.call(
		draftState.sections,
		section,
	)

	const rawValue = hasStoredSection
		? (storedValue ?? sanitizedDefault)
		: sanitizedDefault
	const hasStoredValue =
		hasStoredSection && !isDeepEqual(rawValue, sanitizedDefault)

	const initialParse = schema.safeParse(rawValue)

	const [status, setStatus] = useState<SectionFormStatus>('idle')
	const [errors, setErrors] = useState<readonly z.ZodIssue[]>([])

	const [lastValidValues, setLastValidValues] = useState<Values>(() =>
		initialParse.success ? initialParse.data : sanitizedDefault,
	)

	const [didValidate, setDidValidate] = useState<boolean>(false)
	const didValidateRef = useRef(didValidate)

	useEffect(() => {
		didValidateRef.current = didValidate
	}, [didValidate])

	const valueRef = useRef(rawValue)

	useEffect(() => {
		if (valueRef.current === rawValue) return
		valueRef.current = rawValue
		const nextParse = schema.safeParse(rawValue)
		if (nextParse.success) {
			setLastValidValues(nextParse.data)
			if (didValidateRef.current) {
				setErrors([])
				setStatus('valid')
			} else {
				setErrors([])
				setStatus('idle')
			}
		} else {
			if (didValidateRef.current) {
				setErrors(nextParse.error.issues)
				setStatus('invalid')
			} else {
				setErrors([])
				setStatus('idle')
			}
		}
	}, [rawValue, schema])

	const values = initialParse.success
		? initialParse.data
		: ((rawValue as Values | undefined) ?? sanitizedDefault)

	const setValues = useCallback(
		(action: SetSectionStateAction<Values>) => {
			store.updateSection(section, (previous: unknown) => {
				const resolvedPrevious =
					previous === undefined ? sanitizedDefault : (previous as Values)
				const candidate =
					typeof action === 'function'
						? (action as (value: Values) => Values)(resolvedPrevious)
						: action
				const parsed = schema.safeParse(candidate)
				if (parsed.success) {
					setLastValidValues(parsed.data)
					if (didValidateRef.current) {
						setErrors([])
						setStatus('valid')
					} else {
						setErrors([])
						setStatus('idle')
					}
					return parsed.data
				}
				if (isDeepEqual(candidate, sanitizedDefault)) {
					setErrors([])
					setStatus(didValidateRef.current ? 'valid' : 'idle')
					return candidate
				}
				if (didValidateRef.current) {
					setErrors(parsed.error.issues)
					setStatus('invalid')
				} else {
					setErrors([])
					setStatus('idle')
				}
				return candidate
			})
		},
		[sanitizedDefault, schema, section, store],
	)

	const setFieldValue = useCallback(
		(path: string, value: unknown) => {
			setValues((current) => setValueAtPath(current, path, value))
		},
		[setValues],
	)

	const updateFieldValue = useCallback(
		(path: string, updater: (current: unknown) => unknown) => {
			setValues((current) => updateValueAtPath(current, path, updater))
		},
		[setValues],
	)

	const getFieldValue = useCallback(
		<T = unknown>(path: string) => getValueAtPath<Values, T>(values, path),
		[values],
	)

	const reset = useCallback(() => {
		store.updateSection(section, sanitizedDefault)
		setErrors([])
		setStatus('idle')
		setLastValidValues(sanitizedDefault)
		setDidValidate(false)
		didValidateRef.current = false
	}, [sanitizedDefault, section, store])

	const clear = useCallback(() => {
		store.clearSection(section)
		setErrors([])
		setStatus('idle')
		setLastValidValues(sanitizedDefault)
		setDidValidate(false)
		didValidateRef.current = false
	}, [sanitizedDefault, section, store])

	const submit = useCallback<() => SectionFormSubmission<Values>>(() => {
		const snapshot = store.getState().sections[section]
		const candidate =
			snapshot === undefined ? sanitizedDefault : (snapshot as Values)
		const parsed = schema.safeParse(candidate)
		setDidValidate(true)
		didValidateRef.current = true
		if (parsed.success) {
			setErrors([])
			setStatus('valid')
			setLastValidValues(parsed.data)
			// Fire-and-forget sync of the whole draft to JSONBin
			void syncDraftToJsonBin()
			onValidSubmit?.(parsed.data)
			return {
				valid: true,
				data: parsed.data,
			}
		}
		setErrors(parsed.error.issues)
		setStatus('invalid')
		return {
			valid: false,
			issues: parsed.error.issues,
		}
	}, [onValidSubmit, sanitizedDefault, schema, section, store])

	const fieldErrorMap = useMemo(() => {
		const map = new Map<string, string[]>()
		for (const issue of errors) {
			const pathKey =
				issue.path.length > 0 ? issue.path.map(String).join('.') : '$root'
			const list = map.get(pathKey) ?? []
			list.push(issue.message)
			map.set(pathKey, list)
			const [first] = issue.path
			if (typeof first === 'string' && pathKey !== first) {
				const shallow = map.get(first) ?? []
				shallow.push(issue.message)
				map.set(first, shallow)
			}
		}
		return map
	}, [errors])

	const getFieldErrors = useCallback(
		(path: string): string[] => [...(fieldErrorMap.get(path) ?? [])],
		[fieldErrorMap],
	)

	return {
		values,
		lastValidValues,
		setValues,
		submit,
		reset,
		clear,
		status,
		errors,
		getFieldErrors,
		hasStoredValue,
		setFieldValue,
		updateFieldValue,
		getFieldValue,
	}
}

function isDeepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true
	try {
		return JSON.stringify(a) === JSON.stringify(b)
	} catch {
		return false
	}
}
